import * as React from "react";
import HoldLayoutDsk from "../holdLayout/holdLayoutDsk";
import { RouteChildrenProps } from "react-router-dom";
import { IHoldRouteParams } from "../../../types/components/holdings/holdTypes";
import {
  AGetEquityUsableHoldings,
  AGetEquityUnusableHoldings,
  AGetEquitySmallcaseHoldings,
} from "../../../service/holdings/holdApiService";
import { IHoldTotalBlockProps } from "../generic/holdTotalBlock";
import DeviceContext from "../../../service/indentifyDevice";
import HoldLayoutTab from "../holdLayout/holdLayoutTab";
import HoldLayoutMob from "../holdLayout/holdLayoutMob";
import HoldEqUsableTable from "../holdTables/holdEqUsableTable/holdEqUsableTable";
import HoldEqUnusableTable from "../holdTables/holdEqUnusableTable/holdEqUnusableTable";
import HoldEqSmallcaseTable from "../holdTables/holdEqSmallcaseTable/holdEqSmallcaseTable";
import { EHoldScreenState } from "../holdConstants";

export interface IHoldEquityContainerProps {}

export interface IHoldEquityContainerState {
  usableHoldings?: any; // if unedfined, then api is not called yet
  unusableHoldings?: any; // if unedfined, then api is not called yet
  smallcaseHoldings?: any; // if unedfined, then api is not called yet
  currentRouteParam?: IHoldRouteParams["hold"];
}

class HoldEquityContainer extends React.Component<
  IHoldEquityContainerProps & RouteChildrenProps<IHoldRouteParams>,
  IHoldEquityContainerState
> {
  state: IHoldEquityContainerState = {
    currentRouteParam: this.props.match?.params.hold,
  };

  getEquityUsableHoldingsRes = (res: any) => {
    if (res.data) {
      this.setState({ usableHoldings: res.data });
    } else {
      this.setState({ usableHoldings: {} });
    }
  };

  getEquityUnusableHoldingsRes = (res: any) => {
    if (res.data) {
      this.setState({ unusableHoldings: res.data });
    } else {
      this.setState({ unusableHoldings: {} });
    }
  };

  getEquitySmallcaseHoldingsRes = (res: any) => {
    if (typeof res === "string") {
      this.setState({ smallcaseHoldings: [] });
    } else {
      this.setState({ smallcaseHoldings: res });
    }
  };

  callApi(hold?: IHoldRouteParams["hold"]) {
    // call both usable and unusable holding api for both tabs
    // because we need to show both data on top
    if (hold === "usable" || hold === "unusable") {
      // if data is already present, then there is no need to call api again
      if (this.state.usableHoldings === undefined) {
        AGetEquityUsableHoldings(this.getEquityUsableHoldingsRes);
      }

      if (this.state.unusableHoldings === undefined) {
        AGetEquityUnusableHoldings(this.getEquityUnusableHoldingsRes);
      }
    } else if (hold === "smallcase") {
      if (this.state.smallcaseHoldings === undefined) {
        AGetEquitySmallcaseHoldings(this.getEquitySmallcaseHoldingsRes);
      }
    }
  }

  totalBlockData(): IHoldTotalBlockProps {
    if (
      this.state.currentRouteParam === "usable" ||
      this.state.currentRouteParam === "unusable"
    ) {
      let unusable =
        this.state.unusableHoldings &&
        this.state.unusableHoldings.elssReportIndividualHoldings
          ? this.state.unusableHoldings.elssReportIndividualHoldings
              .reduce((a: number, b: any) => a + +b.holdingValue, 0)
              .toString()
          : 0;
      return {
        usable: this.state.usableHoldings?.eqHv,
        unusable,
        total: (+this.state.usableHoldings?.eqHv + +unusable).toString(),
      };
    } else {
      //console.log(this.state.smallcaseHoldings);
      return {
        total: this.state.smallcaseHoldings
          ? this.state.smallcaseHoldings
              .reduce((a: number, b: any) => a + +b.averagePrice, 0)
              .toString()
          : 0,
      };
    }
  }

  showNoData(): EHoldScreenState {
    if (this.props.match?.params.hold === "usable") {
      if (this.state.usableHoldings === undefined) {
        return EHoldScreenState.LOADING;
      }
      if (
        this.state.usableHoldings.rmsHdg &&
        this.state.usableHoldings.rmsHdg.length
      ) {
        return EHoldScreenState.DATA;
      }
      return EHoldScreenState.NO_DATA;
    }

    if (this.props.match?.params.hold === "unusable") {
      if (this.state.unusableHoldings === undefined) {
        return EHoldScreenState.LOADING;
      }
      if (
        this.state.unusableHoldings.elssReportIndividualHoldings &&
        this.state.unusableHoldings.elssReportIndividualHoldings.length
      ) {
        return EHoldScreenState.DATA;
      }
      return EHoldScreenState.NO_DATA;
    }

    if (this.props.match?.params.hold === "smallcase") {
      if (this.state.smallcaseHoldings === undefined) {
        return EHoldScreenState.LOADING;
      }
      if (this.state.smallcaseHoldings.length) {
        return EHoldScreenState.DATA;
      }
      return EHoldScreenState.NO_DATA;
    }

    return EHoldScreenState.NO_DATA;
  }

  componentDidMount() {
    // call the api initially
    this.setState({ currentRouteParam: this.props.match?.params.hold });
    this.callApi(this.props.match?.params.hold);
  }

  componentDidUpdate() {
    // call api only if route is changed
    if (this.props.match?.params.hold !== this.state.currentRouteParam) {
      this.setState({ currentRouteParam: this.props.match?.params.hold });
      this.callApi(this.props.match?.params.hold);
    }
  }

  render() {
    const child =
      this.props.match?.params.hold === "usable" ? (
        <HoldEqUsableTable
          data={
            this.state.usableHoldings && this.state.usableHoldings.rmsHdg
              ? this.state.usableHoldings.rmsHdg
              : []
          }
        />
      ) : this.props.match?.params.hold === "unusable" ? (
        <HoldEqUnusableTable
          data={
            this.state.unusableHoldings &&
            this.state.unusableHoldings.elssReportIndividualHoldings
              ? this.state.unusableHoldings.elssReportIndividualHoldings
              : []
          }
        />
      ) : (
        <HoldEqSmallcaseTable
          data={
            Array.isArray(this.state.smallcaseHoldings)
              ? this.state.smallcaseHoldings
              : []
          }
        />
      );

    return DeviceContext().isDesktop ? (
      <HoldLayoutDsk
        totalBlockData={this.totalBlockData()}
        screenState={this.showNoData()}
      >
        {child}
      </HoldLayoutDsk>
    ) : DeviceContext().isTablet ? (
      <HoldLayoutTab
        totalBlockData={this.totalBlockData()}
        screenState={this.showNoData()}
      >
        {child}
      </HoldLayoutTab>
    ) : (
      <HoldLayoutMob
        totalBlockData={this.totalBlockData()}
        screenState={this.showNoData()}
      >
        {child}
      </HoldLayoutMob>
    );
  }
}

export default HoldEquityContainer;
